import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { HistoryIcon, ArrowDownIcon, ArrowUpIcon, GiftIcon, RotateCcwIcon, ClockIcon, SearchIcon, FilterIcon } from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Tabs } from '../../components/ui/Tabs';
import { Input } from '../../components/ui/Input';
import { useAuth } from '../../lib/AuthContext';
import { formatVND, formatDateTime } from '../../lib/formatters';
import { MOCK_WALLET_TRANSACTIONS, MOCK_POINTS_TRANSACTIONS } from '../../lib/mockData';
type FilterType = 'all' | 'deposit' | 'purchase' | 'bonus' | 'refund' | 'points';
export function TransactionHistoryPage() {
  const {
    currentCustomer,
    isCustomerLoggedIn
  } = useAuth();
  const [activeFilter, setActiveFilter] = useState<FilterType>('all');
  const [searchQuery, setSearchQuery] = useState('');
  if (!isCustomerLoggedIn || !currentCustomer) {
    return <div className="max-w-4xl mx-auto px-4 py-16 text-center" data-id="element-1787">
        <HistoryIcon className="w-16 h-16 text-[#666666] mx-auto mb-4" data-id="element-1788" />
        <h2 className="text-xl font-semibold text-white mb-2" data-id="element-1789">
          Vui lòng đăng nhập
        </h2>
      </div>;
  }
  const walletTxs = MOCK_WALLET_TRANSACTIONS.filter(t => t.customerId === currentCustomer.id);
  const pointsTxs = MOCK_POINTS_TRANSACTIONS.filter(t => t.customerId === currentCustomer.id);
  const tabs = [{
    id: 'all',
    label: 'Tất cả',
    count: walletTxs.length + pointsTxs.length
  }, {
    id: 'deposit',
    label: 'Nạp tiền',
    count: walletTxs.filter(t => t.type === 'deposit').length
  }, {
    id: 'purchase',
    label: 'Thanh toán',
    count: walletTxs.filter(t => t.type === 'purchase').length
  }, {
    id: 'bonus',
    label: 'Thưởng',
    count: walletTxs.filter(t => t.type === 'bonus').length
  }, {
    id: 'refund',
    label: 'Hoàn tiền',
    count: walletTxs.filter(t => t.type === 'refund').length
  }, {
    id: 'points',
    label: 'Điểm',
    count: pointsTxs.length
  }];
  const allItems = useMemo(() => {
    const wallet = walletTxs.map(t => ({
      ...t,
      source: 'wallet' as const
    }));
    const points = pointsTxs.map(t => ({
      id: t.id,
      customerId: t.customerId,
      type: t.type as string,
      amount: t.points,
      balanceAfter: t.balanceAfter,
      description: t.description,
      createdAt: t.createdAt,
      source: 'points' as const
    }));
    let items = activeFilter === 'points' ? points : activeFilter === 'all' ? [...wallet, ...points] : wallet.filter(t => t.type === activeFilter);
    if (searchQuery) {
      items = items.filter(t => t.description.toLowerCase().includes(searchQuery.toLowerCase()));
    }
    return items.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }, [activeFilter, searchQuery, walletTxs, pointsTxs]);
  const getIcon = (type: string, source: string) => {
    if (source === 'points') return <StarIconSmall data-id="element-1790" />;
    switch (type) {
      case 'deposit':
        return <ArrowDownIcon className="w-4 h-4 text-[#22C55E]" data-id="element-1791" />;
      case 'purchase':
        return <ArrowUpIcon className="w-4 h-4 text-[#EF4444]" data-id="element-1792" />;
      case 'bonus':
        return <GiftIcon className="w-4 h-4 text-[#C8A84E]" data-id="element-1793" />;
      case 'refund':
        return <RotateCcwIcon className="w-4 h-4 text-[#3B82F6]" data-id="element-1794" />;
      default:
        return <ClockIcon className="w-4 h-4 text-[#666666]" data-id="element-1795" />;
    }
  };
  return <div className="max-w-4xl mx-auto px-4 py-8" data-id="element-1796">
      <motion.div initial={{
      opacity: 0,
      y: 12
    }} animate={{
      opacity: 1,
      y: 0
    }} data-id="element-1797">
        <h1 className="text-2xl font-bold text-white mb-6 flex items-center gap-3" data-id="element-1798">
          <HistoryIcon className="w-7 h-7 text-[#C8A84E]" data-id="element-1799" />
          Lịch Sử Giao Dịch
        </h1>
      </motion.div>

      <motion.div initial={{
      opacity: 0,
      y: 12
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.05
    }} data-id="element-1800">
        <Tabs tabs={tabs} activeTab={activeFilter} onTabChange={id => setActiveFilter(id as FilterType)} className="mb-4" data-id="element-1801" />
      </motion.div>

      <motion.div initial={{
      opacity: 0,
      y: 12
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.1
    }} className="mb-4" data-id="element-1802">
        <Input placeholder="Tìm kiếm giao dịch..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} leftIcon={<SearchIcon className="w-4 h-4" data-id="element-1804" />} data-id="element-1803" />
      </motion.div>

      <Card padding="none" data-id="element-1805">
        {allItems.length === 0 ? <div className="text-center py-12 text-[#666666]" data-id="element-1806">
            <HistoryIcon className="w-10 h-10 mx-auto mb-2 opacity-50" data-id="element-1807" />
            <p data-id="element-1808">Không tìm thấy giao dịch</p>
          </div> : <div className="divide-y divide-[#2A2A2A]/50" data-id="element-1809">
            {allItems.map((item, i) => <motion.div key={item.id} initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: i * 0.02
        }} className="flex items-center gap-3 px-5 py-3.5" data-id="element-1810">
                <div className="w-9 h-9 rounded-lg bg-[#141414] flex items-center justify-center flex-shrink-0" data-id="element-1811">
                  {getIcon(item.type, item.source)}
                </div>
                <div className="flex-1 min-w-0" data-id="element-1812">
                  <p className="text-sm text-white truncate" data-id="element-1813">
                    {item.description}
                  </p>
                  <p className="text-xs text-[#666666]" data-id="element-1814">
                    {formatDateTime(item.createdAt)}
                  </p>
                </div>
                <span className={`text-sm font-semibold flex-shrink-0 ${item.amount >= 0 ? 'text-[#22C55E]' : 'text-[#EF4444]'}`} data-id="element-1815">
                  {item.amount >= 0 ? '+' : ''}
                  {item.source === 'points' ? `${item.amount} điểm` : formatVND(item.amount)}
                </span>
              </motion.div>)}
          </div>}
      </Card>
    </div>;
}
function StarIconSmall() {
  return <svg className="w-4 h-4 text-[#C8A84E]" viewBox="0 0 24 24" fill="currentColor" data-id="element-1816">
      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" data-id="element-1817" />
    </svg>;
}